import React, {
  useState,
  MouseEvent,
  KeyboardEvent,
  useEffect,
  useMemo,
  FC,
  ReactNode,
  useRef,
} from 'react';
import Popover, {
  PopoverProps,
} from '@mui/material/Popover';
import IconButton from '@mui/material/IconButton';
import Tooltip from 'core/components/Tooltip';
import {
  FormProvider,
  useFieldArray,
  useForm,
} from 'react-hook-form';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import Button from 'pages/Dashboard/components/Button';
import clsx from 'clsx';

export type EOBCodesComponentProps = {
  index: number;
};

type Props<T> = {
  data: T[];
  readonly?: boolean;
  formKey?: string;
  defaultValues?: T;
  getLabel?: (item: T) => string | ReactNode;
  getPillClassName?: (item: T) => string | undefined;
  getId?: (item: T) => string;
  onSubmit?: (data: T[]) => void;
  CodePopoverComponent: FC<EOBCodesComponentProps>;
  popoverProps?: Partial<PopoverProps>;
  tooltip?: string;
  max?: number;
};

const commonPillClasses = 'bg-slate-100 border text-xs text-center border-slate-300 text-slate-700 font-semibold eob-codes px-1 py-0.5 rounded-md my-1 hover:!border-gray-300 shrink-0 whitespace-nowrap';
const cellPillClasses = clsx(commonPillClasses, '!my-0');
const underlineClassName = '!underline !underline-offset-2 !decoration-dashed !decoration-gray-400';
const focusableSelector = [
  'input:not([disabled])',
  'button:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

type Item = {
  label: string | ReactNode;
  id: string;
  key: string;
  className?: string;
};

type TooltipListProps = {
  items: Item[];
};

function TooltipList({ items }: TooltipListProps) {
  return (
    <div className="flex flex-col gap-0.5 my-1">
      {items.map((item) => (
        <div key={item.key} className={clsx(commonPillClasses, item.className)}>
          {item.label}
        </div>
      ))}
    </div>
  );
}

type Code = 'adjustments' | 'healthCareRemarkCodes';

function getFocusTarget(
  container: HTMLDivElement | null,
  rowIndex: number | null,
) {
  let target: HTMLElement | null = container;

  if (container && rowIndex !== null) {
    target = container.querySelector<HTMLElement>(
      `[data-code-row-index="${rowIndex}"]`,
    ) ?? container;
  }

  return target;
}

function focusFirstInteractiveElement(target: HTMLElement | null) {
  target?.querySelector<HTMLElement>(focusableSelector)?.focus();
}

export default function EOBCodesPopover<T>({
  data,
  readonly,
  formKey = '',
  defaultValues,
  getLabel,
  getPillClassName,
  getId,
  onSubmit,
  CodePopoverComponent,
  popoverProps,
  tooltip,
  max = 1,
}: Props<T>) {
  const formProps = useForm({ mode: 'onChange' });
  const {
    control,
    trigger,
    setValue,
    handleSubmit,
    formState: { isValid },
    reset,
  } = formProps;

  const { fields, append, remove } = useFieldArray({
    name: formKey ?? '',
    control,
  });

  const cellValue = useMemo(() => {
    const normalized = data?.map((item, index) => {
      const id = getId?.(item) ?? '';
      return {
        label: getLabel?.(item) ?? '',
        className: getPillClassName?.(item),
        id,
        key: `${id}-${index}`,
      };
    });
    const dataToShow = normalized?.slice(0, max);
    const remainder = normalized?.slice(max);
    return { dataToShow, remainder };
  }, [data, getId, getLabel, getPillClassName, max, readonly]);

  useEffect(() => {
    setValue(formKey, data);
    if (data?.length === 0) {
      append(defaultValues);
    }
  }, [data]);

  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const popoverContentRef = useRef<HTMLDivElement | null>(null);
  const pendingFocusRowIndexRef = useRef<number | null>(null);
  const openPopover = (target: HTMLDivElement) => {
    setAnchorEl(target.closest('.populate-table-row') as HTMLDivElement);
    if (fields?.length === 0) {
      append(defaultValues);
    }
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    openPopover(event.currentTarget);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (readonly) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      openPopover(event.currentTarget);
    }
  };

  const handleClose = () => {
    reset({ [formKey]: data });
    setAnchorEl(null);
  };

  const submit = (payload: Partial<Record<Code, T[]>>) => {
    const nextData = (payload?.[formKey as Code] ?? []) as T[];
    onSubmit?.(nextData);
    reset({ [formKey]: nextData });
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  useEffect(() => {
    let frameId: number | undefined;

    if (open) {
      frameId = window.requestAnimationFrame(() => {
        const rowIndex = pendingFocusRowIndexRef.current;
        const target = getFocusTarget(popoverContentRef.current, rowIndex);

        focusFirstInteractiveElement(target);
        pendingFocusRowIndexRef.current = null;
      });
    }

    return () => {
      if (typeof frameId !== 'undefined') {
        window.cancelAnimationFrame(frameId);
      }
    };
  }, [fields.length, open]);

  const handleAppend = async () => {
    const isValid = await trigger(formKey, { shouldFocus: true });
    if (isValid) {
      pendingFocusRowIndexRef.current = fields.length;
      append(defaultValues);
    }
  };

  const pillClasses = clsx(
    cellPillClasses,
    { hidden: (data?.length ?? 0) === 0 },
  );
  const lastVisiblePillIndex = (cellValue.dataToShow?.length ?? 0) - 1;

  const buttonClasses = clsx(
    'w-full h-full flex items-stretch text-black border border-transparent -mx-1 px-1 flex-1',
    'select-none',
    'focus:outline-none focus-visible:border-blue-400 focus-visible:ring-2',
    'focus-visible:ring-inset focus-visible:ring-blue-200 focus-visible:rounded',
    {
      'cursor-default': readonly,
      'cursor-pointer pointer-events-auto hover:border-gray-400': !readonly,
    },
  );

  return (
    <>
      <Tooltip
        title={tooltip}
        hide={readonly || (data?.length ?? 0) > 0}
      >
        <div
          className={buttonClasses}
          onClick={readonly ? undefined : handleClick}
          role="button"
          tabIndex={readonly ? -1 : 0}
          onKeyDown={handleKeyDown}
        >
          <div className={clsx(
            'flex w-full h-full py-0.5',
            { 'flex-col items-start gap-1': data?.length > 0 },
          )}
          >
            {data?.length === 0 && (
              <div className={clsx('not-set-placeholder font-semibold whitespace-nowrap leading-none', underlineClassName)}>
                Not set
              </div>
            )}
            {cellValue.dataToShow.map((item, index) => {
              const isLastVisibleItem = index === lastVisiblePillIndex;
              const showRemainder = isLastVisibleItem && cellValue.remainder.length > 0;

              return (
                <div
                  key={item.key}
                  className={clsx(
                    'flex items-center gap-1',
                    { 'w-full': !showRemainder },
                  )}
                >
                  <div className={clsx(pillClasses, item.className, {
                    'pointer-events-none': readonly,
                  })}
                  >
                    {item.label}
                  </div>
                  {showRemainder && (
                    <Tooltip
                      title={<TooltipList items={cellValue.remainder} />}
                    >
                      <span>
                        <div className={pillClasses}>
                          +
                          {cellValue.remainder.length}
                        </div>
                      </span>
                    </Tooltip>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Tooltip>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disableScrollLock
        classes={{
          paper: '!w-[860px]',
        }}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'center',
        }}
        {...popoverProps}
      >
        <FormProvider {...formProps}>
          <div
            ref={popoverContentRef}
            className="flex flex-col relative min-h-[40px] h-auto overflow-hidden"
          >
            <div className="flex flex-1 flex-col m-4 gap-form-row populate-form">
              {fields.map((field, index) => (
                <div
                  key={field.id}
                  data-code-row-index={index}
                  className="flex flex-grow items-end gap-form-field w-full"
                >
                  <CodePopoverComponent key={field.id} index={index} />
                  <div>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton onClick={() => { remove(index); }}>
                          <ClearIcon className="text-gray-400 !text-lg" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </div>
                </div>
              ))}
              <Button
                className="!text-xs !font-medium !w-full !border !border-dashed !border-blue-500 !bg-blue-50 !p-2 !max-w-full"
                onClick={handleAppend}
                variant="outlined"
                color="primary"
              >
                <AddIcon
                  className="mr-1"
                  fontSize="small"
                />
                Add
              </Button>
              <div className="flex gap-2">
                <Button
                  className="!text-xs !font-medium"
                  onClick={handleSubmit(submit)}
                  variant="contained"
                  color="primary"
                  disabled={!isValid}
                  type="submit"
                >
                  Update
                </Button>
                <Button
                  className="!text-xs !font-medium"
                  onClick={handleClose}
                  variant="outlined"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </FormProvider>
      </Popover>
    </>
  );
}
